import { useCreatePackage, useEditPackage } from './../reactQuery/usePackage'
import { PackageInfoForm } from './../../types/index'
import { useIntl, defineMessages } from 'react-intl'
import {
  notiFormForError,
  savePkgConfirm,
} from '../../components/notificationForm'
import {
  filterMultiLangInfos,
  isAnyMultiLangInfoEmpty,
  isAnyMultiLangNameOver40,
} from '../../utils/multiLangInfo'
import * as _ from 'lodash'

import rootHistory from '../../rootHistory'
import isNotPng from '../../utils/isNotPng'
import { packageUploadStageList } from '../../layout/Upload'
import { useHistory } from 'react-router-dom'
import { savePkgNoti } from '../../components/NotiCustom'
import { useIsMutating } from 'react-query'
const messages = defineMessages({
  packageBtn: {
    id: 'PackageInfo.package.btn',
    defaultMessage: 'Save',
  },
  fileSize: {
    id: 'PackageInfo.noti.fileSize',
    defaultMessage: 'The image size is too large. 😨',
  },
  fileWidthHeight: {
    id: 'PackageInfo.png.size',
    defaultMessage: 'Image must be a 250x250px PNG file',
  },
  thumbnail: {
    id: 'PackageInfo.noti.thumbnail',
    defaultMessage: 'Please register package cover image. 🌈',
  },
  thumbnailExt: {
    id: 'PackageInfo.noti.thumbnailExt',
    defaultMessage: ' The pack thumbnail image should be a png file.',
  },
  inputName: {
    id: 'PackageInfo.noti.input.name',
    defaultMessage: 'Please register the collection name. ✏️',
  },
  englishName: {
    id: 'PackageInfo.noti.english.name',
    defaultMessage: 'You must register the package name in English. 😢',
  },
  nameMinLength: {
    id: 'PackageInfo.noti.name.minLength',
    defaultMessage: 'Pack name must be at least 3 characters. 😢',
  },
  nameMaxLength: {
    id: 'PackageInfo.noti.name.maxLength',
    defaultMessage: 'The pack name cannot have more than 40 characters. 😢',
  },
  nameOverlap: {
    id: 'PackageInfo.error.nameOverlap',
    defaultMessage: 'This Pack name has already been used. 😢',
  },
  multiLangInfoNameMinLength: {
    id: 'PackageInfo.noti.multiLangInfo.name.minLength',
    defaultMessage: 'Pack name must be at least 1 characters. 😢',
  },
  multiLangInfoNameMaxLength: {
    id: 'PackageInfo.noti.multiLangInfo.name.maxLength',
    defaultMessage: 'The pack name cannot have more than 40 characters. 😢',
  },
  inputDescription: {
    id: 'PackageInfo.noti.input.description',
    defaultMessage: 'Please register description. 📒',
  },
  englishDescription: {
    id: 'PackageInfo.noti.english.description',
    defaultMessage: 'Description must be registered in English. 😢',
  },
  multiLangInfoDescriptionMinLength: {
    id: 'PackageInfo.noti.multiLangInfo.description.minLength',
    defaultMessage: 'Pack description must be at least 1 characters. 😢',
  },
  languageInputValue: {
    id: 'PackageInfo.noti.languageSelection.input.language',
    defaultMessage: 'Please select a language. 😢',
  },
  noInputKeyText: {
    id: 'PackageInfo.noti.languageSelection.input.keyText.noInput',
    defaultMessage:
      'Pack keyword is not entered.\nPlease enter more than one pack keyword.✍️',
  },
  savePkgNotiErrorTitle: {
    id: 'PackageInfo.noti.savePackage.error.title',
    defaultMessage: 'Save package information!',
  },
  savePkgNotiErrorDescription: {
    id: 'PackageInfo.noti.savePackage.error.description',
    defaultMessage:
      'Package information must be saved before you can move on to the next step.\nClick on the ‘Save’ button to complete this step.',
  },
  notEditNotiErrorTitle: {
    id: 'PackageInfo.noti.editPackage.error.title',
    defaultMessage: 'Please modify package information.',
  },
  notEditNotiErrorDescription: {
    id: 'PackageInfo.noti.editPackage.error.description',
    defaultMessage:
      'Some information has not been modified.\nPress Save button after editing to complete saving.',
  },
  savePkgNotiTitle: {
    id: 'PackageInfo.noti.savePackage.title',
    defaultMessage: 'Package information saved!',
  },
  savePkgNotiDescription: {
    id: 'PackageInfo.noti.savePackage.description',
    defaultMessage:
      'Your package information has been saved.\nYou may move on to the next step: 2. Sticker Upload',
  },
})

export default function useUploadPackageInfo(packageId: string) {
  const intl = useIntl()
  const { formatMessage } = intl
  const isMutatingPackage = useIsMutating(packageId) > 0

  const mutateCreatePackage = useCreatePackage()
  const mutateEditPackage = useEditPackage(packageId)
  const checkRequireFeild = (
    packageInfo: PackageInfoForm,
    isUpdatePackagePage: boolean,
  ): boolean => {
    let isValid = true

    if (!packageInfo.name.en) {
      notiFormForError(intl.locale, formatMessage(messages.inputName))
      isValid = false
    } else if (packageInfo.name.en.length < 3) {
      notiFormForError(intl.locale, formatMessage(messages.nameMinLength))
      isValid = false
    }
    if (
      packageInfo.name.en.match(
        /([^a-zA-Z0-9,./?~`!@#${}%^&*()-_=+'"‘“’”|<>\s])/,
      ) !== null
    ) {
      notiFormForError(intl.locale, formatMessage(messages.englishName))
      isValid = false
    }

    if (packageInfo.name.en.length > 40) {
      notiFormForError(intl.locale, formatMessage(messages.nameMaxLength))
      isValid = false
    }
    const otherNames = filterMultiLangInfos(packageInfo.name)
    if (isAnyMultiLangInfoEmpty(otherNames)) {
      notiFormForError(
        intl.locale,
        formatMessage(messages.multiLangInfoNameMinLength),
      )
      isValid = false
    }

    if (isAnyMultiLangNameOver40(otherNames)) {
      notiFormForError(
        intl.locale,
        formatMessage(messages.multiLangInfoNameMaxLength),
      )
      isValid = false
    }

    if (!packageInfo.description.en) {
      notiFormForError(intl.locale, formatMessage(messages.inputDescription))
      isValid = false
    }
    if (
      packageInfo.description.en.match(
        /([^a-zA-Z0-9,./?~`!@#${}%^&*()-_=+'"‘“’”|<>\s])|(^\s)/,
      ) !== null
    ) {
      notiFormForError(intl.locale, formatMessage(messages.englishDescription))
      isValid = false
    }
    const otherDescriptions = filterMultiLangInfos(packageInfo.description)

    if (isAnyMultiLangInfoEmpty(otherDescriptions)) {
      notiFormForError(
        intl.locale,
        formatMessage(messages.multiLangInfoDescriptionMinLength),
      )
      isValid = false
    }

    if (!isUpdatePackagePage && !packageInfo.thumbnail) {
      notiFormForError(intl.locale, formatMessage(messages.thumbnail))
      isValid = false
    }
    if (packageInfo.keyTexts.length === 0) {
      notiFormForError(intl.locale, formatMessage(messages.noInputKeyText))
      isValid = false
    }
    return isValid
  }
  const createPackage = (formData: FormData, sessionToken: string) => {
    if (isMutatingPackage) return

    mutateCreatePackage.mutate(
      { formData, sessionToken },
      {
        onSuccess: (data) => {
          savePkgNoti(
            // 기존의 함수가 class 형식으로 history를 참고해서 같이 동작할 수 있게 만듬
            'success',
            formatMessage(messages.savePkgNotiTitle),
            formatMessage(messages.savePkgNotiDescription),
          )
          rootHistory.push(
            `/upload/package/${data.objectId}/${packageUploadStageList[1]}`,
          )
        },
      },
    )
    // dispatch(fetchPackageCreate({ formData, sessionToken }))
    //   .unwrap()
    //   .then((createdPackageInfo) => {
    //     if (createdPackageInfo) {
    //       savePkgNoti(
    //         'success',
    //         formatMessage(messages.savePkgNotiTitle),
    //         formatMessage(messages.savePkgNotiDescription),
    //       )
    //       rootHistory.push(
    //         `/upload/package/${createdPackageInfo.objectId}/${packageUploadStageList[1]}`,
    //       )
    //     }
    //   })
  }
  const updatePackage = (
    formData: FormData,
    packageId: string,
    sessionToken: string,
  ) => {
    if (isMutatingPackage) return

    mutateEditPackage.mutate({
      formData,
      packageId,
      sessionToken,
    })
  }
  const checkThumnail = async (file: File) => {
    if (file.size > 500000) {
      //1. 썸네일 용량 확인.
      notiFormForError(intl.locale, formatMessage(messages.fileSize))
      return false
    } else if (file.type !== 'image/png') {
      //2. contentType 확인. 썸네일은 png만 가능함.
      notiFormForError(intl.locale, formatMessage(messages.thumbnail))
      return false
    } else {
      // apng가 아닌 png만 가능
      const blob = file.slice(0, -1, 'image/png')

      const buffer = await blob.arrayBuffer()
      if (isNotPng(buffer)) {
        notiFormForError(intl.locale, formatMessage(messages.thumbnailExt))
        return false
      }
    }
    // width height 250 인지 확인
    const objUrl = window.URL.createObjectURL(file)
    const img = new Image()
    img.src = objUrl
    await img.decode()
    const { width, height } = img
    if (width === 250 && height === 250) {
      return objUrl
    }
    notiFormForError(intl.locale, formatMessage(messages.fileWidthHeight))

    // 못쓰는 objecturl 은 삭제
    window.URL.revokeObjectURL(objUrl)
    return false
  }

  return { checkRequireFeild, createPackage, updatePackage, checkThumnail }
}
